import { isDefined } from "@clipboard-health/util-ts";
import {
  BookabilityDecision,
  type BookabilityStatusItem,
  BookabilityUnmetCriteria,
} from "@src/appV2/OpenShifts/ShiftAction/types";
import { isAxiosError } from "axios";

/**
 * Returns true when the workplace quiz is the only bookability blocker
 * across both anchor and companion shifts.
 * A shift that is already ALLOWED is not a blocker, so we only inspect BLOCKED shifts.
 */
export function isQuizOnlyBlocker(
  anchorStatus: BookabilityStatusItem | undefined,
  companionStatus: BookabilityStatusItem | undefined
): boolean {
  const statuses = [anchorStatus, companionStatus].filter(isDefined);

  if (statuses.length === 0) {
    return false;
  }

  const blockedStatuses = statuses.filter(
    (s) => s.attributes.bookability.decision === BookabilityDecision.BLOCKED
  );

  if (blockedStatuses.length === 0) {
    return false;
  }

  return blockedStatuses.every((s) => {
    const { bookability } = s.attributes;
    if (bookability.decision !== BookabilityDecision.BLOCKED) {
      return false;
    }

    return (
      bookability.unmetCriteria.length === 1 &&
      bookability.unmetCriteria[0] ===
        BookabilityUnmetCriteria.WORKER_HAS_NOT_PASSED_QUIZ_FOR_WORKPLACE_RULES
    );
  });
}

const DEFAULT_ERROR_MESSAGE = "Something went wrong while booking these shifts. Please try again.";

export function getDisplayableErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const message = error.response?.data?.displayableMessage;
    if (typeof message === "string") {
      return message;
    }
  }

  return DEFAULT_ERROR_MESSAGE;
}
